'use client';

import { useState, useEffect } from 'react';
import { EndpointResult } from './types';

interface Props {
  results: EndpointResult[];
  maxBars?: number;
  onTooltip: (result: EndpointResult | null, x: number, y: number, persistent: boolean) => void;
}

const CLEAR_EVENT = 'ns-clear-selection';

export default function ResultBars({ results, maxBars = 50, onTooltip }: Props) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const display: (EndpointResult | null)[] = [];
  while (display.length + results.length < maxBars) display.push(null);
  display.push(...results.slice(-maxBars));

  useEffect(() => {
    const handler = () => setSelectedIdx(null);
    window.addEventListener(CLEAR_EVENT, handler);
    return () => window.removeEventListener(CLEAR_EVENT, handler);
  }, []);

  const handleClick = (result: EndpointResult, x: number, y: number, idx: number) => {
    window.dispatchEvent(new CustomEvent(CLEAR_EVENT));
    if (selectedIdx === idx) {
      setSelectedIdx(null);
      onTooltip(null, x, y, false);
    } else {
      setSelectedIdx(idx);
      onTooltip(result, x, y, true);
    }
  };

  return (
    <div className="flex gap-[2px] items-end h-8">
      {display.map((r, i) =>
        r === null ? (
          <div key={i} className="flex-1 h-full rounded-sm bg-white/10 min-w-[3px]" />
        ) : (
          <div
            key={i}
            className={`flex-1 h-full rounded-sm cursor-pointer transition-colors min-w-[3px] ${
              r.success
                ? selectedIdx === i ? 'bg-green-400' : 'bg-green-500/70 hover:bg-green-400'
                : selectedIdx === i ? 'bg-red-400' : 'bg-red-500/70 hover:bg-red-400'
            }`}
            onMouseEnter={(e) => onTooltip(r, e.clientX, e.clientY, false)}
            onMouseMove={(e) => onTooltip(r, e.clientX, e.clientY, selectedIdx === i)}
            onMouseLeave={() => { if (selectedIdx !== i) onTooltip(null, 0, 0, false); }}
            onClick={(e) => handleClick(r, e.clientX, e.clientY, i)}
          />
        )
      )}
    </div>
  );
}
